/* Copyright 2016 Google Inc. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
==============================================================================*/

import {Example2D, cap} from "./dataset";
import * as d3 from 'd3';
import * as nn from "./nn";

// Interface definition moved to module level
interface PredictiveNetworkWrapper {
  predict: (point: Example2D) => number;
}

export interface HeatMapSettings {
  [key: string]: any;
  showAxes?: boolean;
  noSvg?: boolean;
  leftPadding?: number;
  topPadding?: number;
}

/** Number of different shades (colors) when drawing a gradient heatmap */
const NUM_SHADES = 30;

/** Pads a string with leading characters to reach a specified length */
function padStart(str: string, targetLength: number, padChar: string): string {
  while (str.length < targetLength) {
    str = padChar + str;
  }
  return str;
}

/**
 * Draws a heatmap using canvas. Used for showing the learned decision
 * boundary of the classification algorithm. Can also draw data points
 * using an svg overlayed on top of the canvas heatmap.
 */
export class HeatMap {
  private settings: HeatMapSettings = {
    showAxes: false,
    noSvg: false,
    leftPadding: 0,
    topPadding: 0
  };
  private xScale;
  private yScale;
  private numSamples: number;
  private color;
  private canvas;
  private svg;
  private topRowRect: d3.Selection<SVGElement>;
  private width: number;
  private height: number;
  private leftPadding: number;
  private topPadding: number;
  private payload: number[];

  constructor(
      width: number, numSamples: number, xDomain: [number, number],
      yDomain: [number, number], container,
      userSettings?: HeatMapSettings) {
    this.numSamples = numSamples;
    let height = width;

    if (userSettings != null) {
      for (let prop in userSettings) {
        this.settings[prop] = userSettings[prop];
      }
    }

    // Calculate padding: use custom padding if provided, otherwise default based on showAxes
    let padding = this.settings.showAxes ? 20 : 0;
    this.leftPadding = this.settings.leftPadding || padding;
    this.topPadding = this.settings.topPadding || (this.settings.showAxes ? 100 : 0);
    // Extra offset for two-line top labels (binary + target function value)
    // Only apply when SVG is enabled (main heatmap with labels), not for node heatmaps
    const topLabelOffset = this.settings.noSvg ? 0 : 12;
    const contentTop = this.topPadding + topLabelOffset;
    this.width = width;
    this.height = height;

    this.xScale = d3.scale.linear()
      .domain(xDomain)
      .range([this.leftPadding, width - padding]);

    this.yScale = d3.scale.linear()
      .domain(yDomain)
      .range([height - padding, contentTop]);

    let tmpScale = d3.scale.linear<string, number>()
        .domain([-1, 0, 1, 2, 3, 4])
        .range(["#d97777", "#b470d5", "#dbd977", "#77c9a4", "#77c9db", "#0877bd"])
        .clamp(true);
    let colors = d3.range(-1, 4 + 1E-9, 5 / NUM_SHADES).map(a => {
      return tmpScale(a);
    });
    this.color = d3.scale.quantize()
                     .domain([-1, 4])
                     .range(colors);

    container = container.append("div")
      .style({
        width: `${width}px`,
        height: `${height}px`,
        position: "relative",
        top: "0px",
        left: "0px"
      });
    this.canvas = container.append("canvas")
      .attr("width", numSamples)
      .attr("height", numSamples)
      .style("width", (width - this.leftPadding - padding) + "px")
      .style("height", (height - contentTop - padding) + "px")
      .style("position", "absolute")
      .style("top", `${contentTop}px`)
      .style("left", `${this.leftPadding}px`);

    if (!this.settings.noSvg) {
      this.svg = container.append("svg").attr({
          "width": width,
          "height": height
      }).style({
        "position": "absolute",
        "left": "0",
        "top": "0"
      }).append("g")
        .attr("transform", `translate(0,0)`);

      this.svg.append("g").attr("class", "train");
      this.svg.append("g").attr("class", "test");
      this.svg.append("g").attr("class", "bit-axis-labels");
    }

    if (this.settings.showAxes) {
      let xAxis = d3.svg.axis()
        .scale(this.xScale)
        .orient("bottom");
      let yAxis = d3.svg.axis()
        .scale(this.yScale)
        .orient("right");
      this.svg.append("g")
        .attr("class", "x axis")
        .attr("transform", `translate(0,${height - 2 * padding})`)
        .call(xAxis);
      this.svg.append("g")
        .attr("class", "y axis")
        .attr("transform", "translate(" + (width - 2 * padding) + ",0)")
        .call(yAxis);
    }
  }

  /**
   * Updates the color scale based on the model type.
   * Oblivious models use range [-1, 4], aware models use range [-1, 15].
   * Purple is always at 0, red at -1, and blue at maxValue.
   * @param modelType - The selected model (e.g., "honest-prefix-aware")
   */
  updateColorScale(modelType: string): void {
    const isAware = modelType.includes("aware");
    const maxValue = isAware ? 15 : 4;

    // Use the same domain as the legend: [-1, 0, 1, 2, 3, 4] or [-1, 0, 5, 10, 15]
    const domain = maxValue === 4
      ? [-1, 0, 1, 2, 3, 4]
      : [-1, 0, 5, 10, 15];

    // Use linear scale directly - it will interpolate correctly between domain points
    this.color = d3.scale.linear<string, number>()
        .domain(domain)
        .range(["#d97777", "#b470d5", "#dbd977", "#77c9a4", "#77c9db", "#0877bd"])
        .clamp(true);
  }

  /**
   * Sets the current payload and updates the arrow indicator.
   * @param payload Array of 4 values (0.0 or 1.0) representing the payload bits
   */
  setPayload(payload: number[]): void {
    this.payload = payload;
    if (payload && payload.length === 4) {
      this.drawPayloadArrow(payload);
    }
  }

  /**
   * Draws an arrow indicator above the top labels showing the current payload position.
   * @param payload Array of 4 values (0.0 or 1.0) representing the payload bits
   */
  drawPayloadArrow(payload: number[]): void {
    if (this.settings.noSvg || !this.svg || !payload || payload.length !== 4) {
      return;
    }

    const labelsGroup = this.svg.select("g.bit-axis-labels");
    
    // Remove any existing arrow
    labelsGroup.selectAll(".payload-arrow").remove();

    // Convert payload to integer index (0-15)
    let index = 0;
    for (let i = 0; i < 4; i++) {
      index = index * 2 + (payload[i] >= 0.5 ? 1 : 0);
    }

    // Calculate x position
    const xRange = this.xScale.range();
    const numLabels = 16;
    const cellWidth = (xRange[1] - xRange[0]) / numLabels;
    const x = xRange[0] + (index + 0.5) * cellWidth;

    // Draw arrow (downward pointing triangle)
    const arrowSize = 6;
    const arrowY = this.topPadding - 18; // Position above the labels
    
    labelsGroup.append("path")
      .attr("class", "payload-arrow")
      .attr("d", `M ${x} ${arrowY + arrowSize} L ${x - arrowSize} ${arrowY} L ${x + arrowSize} ${arrowY} Z`)
      .style("fill", "#000000")
      .style("stroke", "#000000")
      .style("stroke-width", "1");
  }

  /**
   * Renders bit axis labels for the 16x16 grid.
   * Left axis: prefix bits (0000-1111)
   * Top axis: payload bits (0000-1111)
   * @param payload Optional payload to draw arrow indicator
   */
  renderBitAxisLabels(model: string, payload?: number[]): void {
    if (this.settings.noSvg || !this.svg) {
      return;
    }

    const numLabels = 16; // 2^4 = 16 possible 4-bit patterns
    const labelsGroup = this.svg.select("g.bit-axis-labels");

    // Remove any existing labels
    labelsGroup.selectAll("*").remove();

    // Calculate cell size based on the scale range
    const xRange = this.xScale.range();
    const yRange = this.yScale.range();
    const cellWidth = (xRange[1] - xRange[0]) / numLabels;
    const cellHeight = (yRange[0] - yRange[1]) / numLabels; // yRange is inverted

    // Render LEFT axis labels (prefix bits) without popcount
    for (let i = 0; i < numLabels; i++) {
      const binaryStr = padStart(i.toString(2), 4, '0');
      const y = yRange[0] - (i + 0.5) * cellHeight;

      const label = labelsGroup.append("text")
        .attr("class", "bit-axis-label prefix-label")
        .attr("x", this.leftPadding - 5)
        .attr("y", y)
        .attr("text-anchor", "end")
        .attr("dominant-baseline", "middle")
        .style("font-family", "monospace")
        .style("font-size", "9px")
        .style("fill", "#666")
        .text(binaryStr);
    }

    // Render TOP axis labels (payload bits) with target function
    // Always show weighted sum (aware model's cap) since it's more informative
    for (let i = 0; i < numLabels; i++) {
      const binaryStr = padStart(i.toString(2), 4, '0');
      // Convert binary string to payload values (MSB to LSB order)
      const payloadValues = binaryStr.split('').map(c => c === '1' ? 1.0 : 0.0);
      // Calculate cap using aware model (weighted sum)
      const targetValue = Math.round(cap(payloadValues, model));
      const x = xRange[0] + (i + 0.5) * cellWidth;

      const label = labelsGroup.append("text")
        .attr("class", "bit-axis-label payload-label")
        .attr("x", x)
        .attr("y", this.topPadding - 5)
        .attr("text-anchor", "middle")
        .style("font-family", "monospace")
        .style("font-size", "9px")
        .style("fill", "#666");
      
      label.append("tspan")
        .attr("x", x)
        .attr("dy", 0)
        .text(binaryStr);
      
      label.append("tspan")
        .attr("x", x)
        .attr("dy", "1.1em")
        .text(`${targetValue}`);
    }

    // Render "Payload" title above the top axis (X-axis title)
    labelsGroup.append("text")
      .attr("class", "axis-title payload-title")
      .attr("x", (xRange[0] + xRange[1]) / 2)
      .attr("y", 5)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "hanging")
      .style("font-family", "monospace")
      .style("font-size", "9px")
      .style("fill", "#666")
      .text("Payload");

    // Render "Prefix" title to the left of the left axis (Y-axis title, rotated)
    const prefixY = (yRange[0] + yRange[1]) / 2;
    labelsGroup.append("text")
      .attr("class", "axis-title prefix-title")
      .attr("x", 10)
      .attr("y", prefixY)
      .attr("text-anchor", "middle")
      .attr("transform", `rotate(-90, 10, ${prefixY})`)
      .style("font-family", "monospace")
      .style("font-size", "9px")
      .style("fill", "#666")
      .text("Context prefix");

    // Draw payload arrow indicator
    if (payload || this.payload) {
      this.drawPayloadArrow(payload || this.payload);
    }
  }

  updateTestPoints(points: Example2D[], network?: any): void {
    if (this.settings.noSvg) {
      throw Error("Can't add points since noSvg=true");
    }
    this.updateCircles(this.svg.select("g.test"), points, network);
  }

  updatePoints(points: Example2D[], model: string, network?: any): void {
    if (this.settings.noSvg) {
      throw Error("Can't add points since noSvg=true");
    }
    this.updateCircles(this.svg.select("g.train"), points, network);
    this.renderBitAxisLabels(model);
  }

  updateBackground(data: number[][]): void {
    let dx = data[0].length;
    let dy = data.length;
    if (dx !== this.numSamples || dy !== this.numSamples) {
      throw new Error(
          "The provided data matrix must be of size " +
          "numSamples X numSamples");
    }
    let context = (this.canvas.node() as HTMLCanvasElement).getContext("2d");
    let image = context.createImageData(dx, dy);

    for (let y = 0, p = -1; y < dy; ++y) {
      for (let x = 0; x < dx; ++x) {
        let value = data[x][y];
        let c = d3.rgb(this.color(value));
        image.data[++p] = c.r;
        image.data[++p] = c.g;
        image.data[++p] = c.b;
        image.data[++p] = 160;
      }
    }
    context.putImageData(image, 0, 0);
  }

  drawTopRowHighlight(allPoints: Example2D[], network?: PredictiveNetworkWrapper): void {
    if (this.settings.noSvg || !this.svg) {
      return;
    }
    if (this.topRowRect) {
      this.topRowRect.remove();
      this.topRowRect = null;
    }
    if (!allPoints || allPoints.length === 0) {
      return;
    }
    const xDomain = this.xScale.domain();
    const yDomain = this.yScale.domain();
    const visiblePoints = allPoints.filter(p => {
      return p.x >= xDomain[0] && p.x <= xDomain[1] &&
             p.y >= yDomain[0] && p.y <= yDomain[1];
    });
    if (visiblePoints.length === 0) {
      return;
    }
    let maxYValue = -Infinity;
    visiblePoints.forEach(p => {
      if (p.y > maxYValue) {
        maxYValue = p.y;
      }
    });
    const epsilon = 0.0001;
    const topRowPoints = visiblePoints.filter(p => Math.abs(p.y - maxYValue) < epsilon);
    if (topRowPoints.length === 0) {
      return;
    }
    let minX = Infinity, maxX = -Infinity;
    topRowPoints.forEach(p => {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
    });
    const pointYValue = maxYValue;
    const padding = 0.3;
    const rectX = this.xScale(minX - padding);
    const rectY = this.yScale(pointYValue + padding);
    const rectWidth = this.xScale(maxX + padding) - this.xScale(minX - padding);
    const rectHeight = this.yScale(pointYValue - padding) - this.yScale(pointYValue + padding);

    if (rectWidth <= 0 || rectHeight <= 0) return;

    // Rectangle drawing removed - no longer displaying top row indicator
  }

  private bitCount(bits: boolean[]): number {
    let count = 0;
    for (let i = 0; i < bits.length; i++) {
      if (bits[i]) {
        count++;
      }
    }
    return count;
  }

  private updateCircles(container, points: Example2D[], network?: any) {
    let xDomain = this.xScale.domain();
    let yDomain = this.yScale.domain();
    points = points.filter(p => {
      return p.x >= xDomain[0] && p.x <= xDomain[1]
        && p.y >= yDomain[0] && p.y <= yDomain[1];
    });
    container.selectAll("g.point").remove();
    let pointGroups = container.selectAll("g.point")
      .data(points)
      .enter()
      .append("g")
      .attr("class", "point")
      .attr("transform", (d: Example2D) =>
        `translate(${this.xScale(d.x)},${this.yScale(d.y)})`);
    pointGroups.append("circle")
      .attr("r", 8)
      .style("fill", d => {
        const color = this.color(d.label);
        return color;
      })
      .style("stroke", "black")
      .style("stroke-width", "1px");
    // Popcount is now displayed on the axis labels
    if (network) {
      pointGroups.append("circle")
        .attr("r", 8)
        .style("fill", "none")
        .style("stroke", "red")
        .style("stroke-width", "2px")
        .style("opacity", d => {
          // Get the network's raw output for this point
          let networkOutput = network.predict(d);
          // d.label is cap(payload), which is what we want to compare against
          let expectedCap = d.label;
          // Red circle if network output differs from expected cap
          return Math.abs(networkOutput - expectedCap) < 0.5 ? 0 : 1;
        });
    }
  }
}

export function reduceMatrix(matrix: number[][], factor: number): number[][] {
  if (matrix.length !== matrix[0].length) {
    throw new Error("The provided matrix must be a square matrix");
  }
  if (matrix.length % factor !== 0) {
    throw new Error("The width/height of the matrix must be divisible by " +
        "the reduction factor");
  }
  let result: number[][] = new Array(matrix.length / factor);
  for (let i = 0; i < matrix.length; i += factor) {
    result[i / factor] = new Array(matrix.length / factor);
    for (let j = 0; j < matrix.length; j += factor) {
      let avg = 0;
      for (let k = 0; k < factor; k++) {
        for (let l = 0; l < factor; l++) {
          avg += matrix[i + k][j + l];
        }
      }
      avg /= (factor * factor);
      result[i / factor][j / factor] = avg;
    }
  }
  return result;
}
